import { ApiService } from "./api";
import { RemoteLogger } from "./remoteLogger";

export interface PresignRequest {
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadDir: "profile" | "document" | "verification" | "organization_logo" | "USER_CATEGORY_ICON" | "apk";
}

export interface PresignResponse {
  success: boolean;
  data: {
    uploadUrl: string;
    s3Key: string;
    expiresIn: number;
  };
}

export interface RegisterRequest {
  s3Key: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadDir: string;
}

export interface RegisterResponse {
  success: boolean;
  data: {
    id: number;
    uuid: string;
    publicUrl: string;
    mediaType: number;
  };
}

export interface Flow2InitializeRequest {
  purpose: "verification_creation";
  templateId: number;
  fieldId: number;
}

export interface Flow2InitializeResponse {
  uploadUrl: string;
  uuid: string;
}

export class UploadService {
  /**
   * Flow 2 - Step 1: Initialize S3 Upload for Template Field
   */
  static async initializeFlow2Upload(
    data: Flow2InitializeRequest,
    token: string
  ) {
    return await ApiService.post<Flow2InitializeResponse>("/files", data, token);
  }

  /**
   * Flow 2 - Step 2: Upload raw binary content to S3 presigned URL
   */
  static async uploadToS3(
    uploadUrl: string,
    binaryData: Blob | ArrayBuffer | Uint8Array,
    mimeType: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      RemoteLogger.info(`➡ PUT binary data to S3: ${uploadUrl.split("?")[0]}`);
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": mimeType,
        },
        body: binaryData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        RemoteLogger.error(`❌ S3 upload failed with status ${response.status}:`, errorText);
        return { success: false, error: `S3 upload failed: ${response.statusText}` };
      }

      RemoteLogger.info(`✅ S3 upload successful`);
      return { success: true };
    } catch (error: any) {
      RemoteLogger.error("💥 S3 upload network error:", error);
      return { success: false, error: error?.message || "S3 network error" };
    }
  }

  /**
   * Flow 2 - Step 3: Verify & Complete S3 Upload
   */
  static async completeFlow2Upload(
    uuid: string,
    token: string
  ) {
    return await ApiService.post<any>(`/files/${uuid}/complete`, {}, token);
  }

  /**
   * Step 1: Request presigned URL from NestJS backend (Flow 1)
   */
  static async requestPresignedUrl(
    data: PresignRequest,
    token: string
  ) {
    return await ApiService.post<PresignResponse>("/upload/presign", data, token);
  }

  /**
   * Step 3: Register upload in database (Flow 1)
   */
  static async registerMedia(
    data: RegisterRequest,
    token: string
  ) {
    return await ApiService.post<RegisterResponse>("/upload/register", data, token);
  }

  /**
   * Helper to perform the full 3-step upload protocol for a file represented as base64 (Flow 1)
   */
  static async uploadAttachment(
    fileName: string,
    mimeType: string,
    base64Data: string,
    token: string
  ): Promise<{ success: boolean; mediaId?: number; error?: string }> {
    try {
      // 1. Convert base64 to Blob
      const binary = atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const fileBlob = new Blob([bytes], { type: mimeType });

      // 2. Request presigned URL
      const presignResponse = await this.requestPresignedUrl(
        {
          fileName,
          mimeType,
          fileSize: fileBlob.size,
          uploadDir: "verification",
        },
        token
      );

      if (!presignResponse.data || !presignResponse.data.success) {
        return {
          success: false,
          error: presignResponse.error || "Failed to request presigned URL",
        };
      }

      const { uploadUrl, s3Key } = presignResponse.data.data;

      // 3. Upload to S3
      const s3Result = await this.uploadToS3(uploadUrl, fileBlob, mimeType);
      if (!s3Result.success) {
        return {
          success: false,
          error: s3Result.error || "Failed to upload file to S3",
        };
      }

      // 4. Register media
      const registerResponse = await this.registerMedia(
        {
          s3Key,
          fileName,
          mimeType,
          fileSize: fileBlob.size,
          uploadDir: "verification",
        },
        token
      );

      if (!registerResponse.data || !registerResponse.data.success) {
        return {
          success: false,
          error: registerResponse.error || "Failed to register media in database",
        };
      }

      return {
        success: true,
        mediaId: registerResponse.data.data.id,
      };
    } catch (e: any) {
      RemoteLogger.error(`Error in uploadAttachment for ${fileName}:`, e);
      return {
        success: false,
        error: e.message || "An unexpected error occurred during upload",
      };
    }
  }

  /**
   * Helper to perform the Flow 2 upload protocol for verification request creation
   */
  static async uploadAttachmentFlow2(
    fileName: string,
    mimeType: string,
    base64Data: string,
    templateId: number,
    fieldId: number,
    token: string
  ): Promise<{ success: boolean; uuid?: string; error?: string }> {
    try {
      // 1. Convert base64 to Blob
      const binary = atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const fileBlob = new Blob([bytes], { type: mimeType });

      // 2. Initialize S3 Upload for Template Field
      const initResponse = await this.initializeFlow2Upload(
        {
          purpose: "verification_creation",
          templateId,
          fieldId,
        },
        token
      );

      // Support direct or wrapped response format
      const resData = initResponse.data;
      const uuid = resData?.uuid || (resData as any)?.data?.uuid;
      const uploadUrl = resData?.uploadUrl || (resData as any)?.data?.uploadUrl;

      if (!uuid || !uploadUrl) {
        return {
          success: false,
          error: initResponse.error || "Failed to initialize Flow 2 S3 upload (UUID or uploadUrl missing)",
        };
      }

      // 3. Upload raw binary to S3
      const s3Result = await this.uploadToS3(uploadUrl, fileBlob, mimeType);
      if (!s3Result.success) {
        return {
          success: false,
          error: s3Result.error || "Failed to upload binary to S3 (Flow 2)",
        };
      }

      // 4. Verify & Complete S3 Upload
      const completeResponse = await this.completeFlow2Upload(uuid, token);
      if (completeResponse.error) {
        return {
          success: false,
          error: completeResponse.error || "Failed to complete Flow 2 upload verification",
        };
      }

      return {
        success: true,
        uuid,
      };
    } catch (e: any) {
      RemoteLogger.error(`Error in uploadAttachmentFlow2 for ${fileName}:`, e);
      return {
        success: false,
        error: e.message || "An unexpected error occurred during Flow 2 upload",
      };
    }
  }
}
